import { crc16Ccitt } from "./crc16";
import type { SerialTransport } from "../transport/serial-transport";

/** Outer frame markers used by the firmware UART protocol. */
const FRAME_HEADER = 0xcdab;
const FRAME_TAIL_0 = 0xdc;
const FRAME_TAIL_1 = 0xba;

const CMD_SESSION_INIT = 0x0514;
const CMD_ALT_SESSION_INIT = 0x052f;
const REPLY_VERSION = 0x0515;
const CMD_EEPROM_READ = 0x051b;
const REPLY_EEPROM_READ = 0x051c;
const CMD_EEPROM_WRITE = 0x051d;
const REPLY_EEPROM_WRITE = 0x051e;
const CMD_RESET = 0x05dd;
const CMD_READ_ADC = 0x0529;
const REPLY_READ_ADC = 0x052a;
const CMD_SCREENSHOT_INFO = 0x0610;
const REPLY_SCREENSHOT_INFO = 0x0611;
const CMD_SCREENSHOT_CHUNK = 0x0612;
const REPLY_SCREENSHOT_CHUNK = 0x0613;
const CMD_LOGO_RESET = 0x0638;
const REPLY_LOGO_RESET = 0x0639;
const CMD_DEVICE_ID = 0x0640;
const REPLY_DEVICE_ID = 0x0641;
const RUNTIME_XOR_KEY = new Uint8Array([
  0x16, 0x6c, 0x14, 0xe6, 0x2e, 0x91, 0x0d, 0x40,
  0x21, 0x35, 0xd5, 0x40, 0x13, 0x03, 0xe9, 0x80
]);

export const MAX_EEPROM_CHUNK = 128;
export const MAX_SCREENSHOT_CHUNK = 128;
export const SCREENSHOT_FORMAT_ST7565_PAGED = 0;
const EEPROM_READ_RETRY_DELAY_MS = 300;

/** Parsed result of the session-init/version reply. */
export interface VersionInfo {
  version: string;
  hasCustomAesKey: boolean;
  isLocked: boolean;
  challengeWords: [number, number, number, number];
}

export interface ScreenshotInfo {
  width: number;
  height: number;
  totalSize: number;
  pageCount: number;
  format: number;
}

/** Canonical 24-character representation of the PY32 factory UID. */
export function formatDeviceId(words: readonly [number, number, number]): string {
  return words.map((word) => word.toString(16).toUpperCase().padStart(8, "0")).join("");
}

function sanitizeVersionString(raw: Uint8Array): string {
  const decoded = new TextDecoder()
    .decode(raw)
    .replace(/\0+$/, "")
    .replace(/[^\x20-\x7E]/g, "")
    .trim();

  // Prefer canonical "<name> vX.Y.Z" when present and drop protocol noise.
  const semverMatch = decoded.match(/([A-Za-z0-9][A-Za-z0-9 ._+\-]*\sv\d+\.\d+\.\d+)/i);
  if (semverMatch?.[1]) {
    return semverMatch[1].trim();
  }

  // Fallback: trim obvious trailing punctuation/noise.
  return decoded.replace(/[?~`!@#$%^&*]+$/g, "").trim();
}

function randomTimestamp(): number {
  return (Math.random() * 0x1_0000_0000) >>> 0;
}

function concatArrays(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

function isReadTimeout(error: unknown): boolean {
  return error instanceof Error && error.message.startsWith("Timed out waiting for ");
}

/**
 * Low-level telemetry firmware protocol client.
 *
 * This class handles framing/CRC and command/reply validation over a transport.
 */
export class TelemetryProtocolClient {
  private timestamp = randomTimestamp();
  private readonly obfuscate: boolean;

  constructor(private readonly transport: SerialTransport, options?: { obfuscate?: boolean }) {
    this.obfuscate = options?.obfuscate !== false;
  }

  private xorObfuscate(data: Uint8Array): Uint8Array {
    if (!this.obfuscate) {
      return data;
    }
    const out = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i += 1) {
      out[i] = data[i] ^ RUNTIME_XOR_KEY[i % RUNTIME_XOR_KEY.length];
    }
    return out;
  }

  private buildFrame(commandId: number, payload: Uint8Array): Uint8Array {
    const inner = new Uint8Array(4 + payload.length);
    const innerView = new DataView(inner.buffer, inner.byteOffset, inner.byteLength);
    innerView.setUint16(0, commandId, true);
    innerView.setUint16(2, payload.length, true);
    inner.set(payload, 4);

    const crc = crc16Ccitt(inner);
    const bodyWithCrc = new Uint8Array(inner.length + 2);
    const bodyWithCrcView = new DataView(
      bodyWithCrc.buffer,
      bodyWithCrc.byteOffset,
      bodyWithCrc.byteLength
    );
    bodyWithCrc.set(inner, 0);
    bodyWithCrcView.setUint16(inner.length, crc, true);
    const encodedBodyWithCrc = this.xorObfuscate(bodyWithCrc);
    const frame = new Uint8Array(4 + encodedBodyWithCrc.length + 2);
    const frameView = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);

    frameView.setUint16(0, FRAME_HEADER, true);
    frameView.setUint16(2, inner.length, true);
    frame.set(encodedBodyWithCrc, 4);
    frame[frame.length - 2] = FRAME_TAIL_0;
    frame[frame.length - 1] = FRAME_TAIL_1;

    return frame;
  }

  private async readReply(expectedReplyId: number | readonly number[]): Promise<Uint8Array> {
    // Resynchronize on frame boundary if there are stale/noisy bytes in the
    // serial stream before the next valid protocol frame.
    let header = await this.transport.readExact(4);
    let headerView = new DataView(header.buffer, header.byteOffset, header.byteLength);
    let frameId = headerView.getUint16(0, true);
    let bodySize = headerView.getUint16(2, true);
    let discarded = 0;

    while (frameId !== FRAME_HEADER) {
      if (discarded >= 1024) {
        throw new Error(
          `Unable to synchronize protocol frame header after discarding ${discarded} bytes; last header was 0x${frameId.toString(16).toUpperCase()}`
        );
      }
      const nextByte = await this.transport.readExact(1);
      const shifted = new Uint8Array(4);
      shifted.set(header.slice(1), 0);
      shifted[3] = nextByte[0];
      header = shifted;
      headerView = new DataView(header.buffer, header.byteOffset, header.byteLength);
      frameId = headerView.getUint16(0, true);
      bodySize = headerView.getUint16(2, true);
      discarded += 1;
    }

    const encodedBodyWithCrc = await this.transport.readExact(bodySize + 2);
    const footer = await this.transport.readExact(2);
    if (footer[0] !== FRAME_TAIL_0 || footer[1] !== FRAME_TAIL_1) {
      throw new Error("Unexpected frame footer");
    }

    const bodyWithCrc = this.xorObfuscate(encodedBodyWithCrc);
    // Runtime behavior varies across firmware branches; match spot_cli and
    // rely on framing/reply-id/payload-length checks for compatibility.
    const body = bodyWithCrc.slice(0, bodySize);

    const bodyView = new DataView(body.buffer, body.byteOffset, body.byteLength);
    const replyId = bodyView.getUint16(0, true);
    const payloadSize = bodyView.getUint16(2, true);

    const expectedReplyIds = Array.isArray(expectedReplyId) ? expectedReplyId : [expectedReplyId];
    if (!expectedReplyIds.includes(replyId)) {
      const expectedText = expectedReplyIds.map((id) => `0x${id.toString(16).toUpperCase()}`).join(", ");
      throw new Error(
        `Expected reply ${expectedText}, got 0x${replyId.toString(16).toUpperCase()}`
      );
    }

    const payload = body.slice(4);
    if (payload.length !== payloadSize) {
      throw new Error(`Reply payload size mismatch: header=${payloadSize}, actual=${payload.length}`);
    }

    return payload;
  }

  private async exchange(
    commandId: number,
    payload: Uint8Array,
    expectedReplyId: number | readonly number[]
  ): Promise<Uint8Array> {
    await this.transport.clearInputBuffer();
    const frame = this.buildFrame(commandId, payload);
    await this.transport.writeAll(frame);
    return this.readReply(expectedReplyId);
  }

  /** Sends a command that does not provide a reply frame. */
  private async sendNoReply(commandId: number, payload: Uint8Array): Promise<void> {
    const frame = this.buildFrame(commandId, payload);
    await this.transport.writeAll(frame);
  }

  /** Opens a protocol session and returns firmware/version metadata. */
  async startSession(): Promise<VersionInfo> {
    this.timestamp = randomTimestamp();
    const payload = new Uint8Array(4);
    new DataView(payload.buffer).setUint32(0, this.timestamp, true);

    let reply: Uint8Array;
    try {
      reply = await this.exchange(CMD_SESSION_INIT, payload, REPLY_VERSION);
    } catch {
      // Some firmware variants use an alternate session-init command.
      reply = await this.exchange(CMD_ALT_SESSION_INIT, payload, REPLY_VERSION);
    }
    if (reply.length < 36) {
      throw new Error(`Version reply too short: ${reply.length}`);
    }

    const version = sanitizeVersionString(reply.slice(0, 16));

    const view = new DataView(reply.buffer, reply.byteOffset, reply.byteLength);
    return {
      version,
      hasCustomAesKey: reply[16] !== 0,
      isLocked: reply[17] !== 0,
      challengeWords: [
        view.getUint32(20, true),
        view.getUint32(24, true),
        view.getUint32(28, true),
        view.getUint32(32, true)
      ]
    };
  }

  /** Reads the PY32F071 factory-programmed 96-bit unique device ID. */
  async readDeviceId(): Promise<string> {
    const reply = await this.exchange(CMD_DEVICE_ID, new Uint8Array(0), REPLY_DEVICE_ID);
    if (reply.length !== 12) {
      throw new Error(`Device ID reply must contain 12 bytes, got ${reply.length}`);
    }
    const view = new DataView(reply.buffer, reply.byteOffset, reply.byteLength);
    return formatDeviceId([
      view.getUint32(0, true),
      view.getUint32(4, true),
      view.getUint32(8, true)
    ]);
  }

  /**
   * Reads an EEPROM range, chunking requests as needed.
   *
   * `offset` is a 16-bit EEPROM address, `size` is the total byte count.
   */
  async readEeprom(offset: number, size: number): Promise<Uint8Array> {
    if (size < 0) {
      throw new Error("size must be non-negative");
    }
    if (offset < 0 || offset > 0xffff) {
      throw new Error("offset must fit in 16 bits");
    }

    let remaining = size;
    let currentOffset = offset;
    const chunks: Uint8Array[] = [];

    while (remaining > 0) {
      const chunkSize = Math.min(remaining, MAX_EEPROM_CHUNK);
      const payload = new Uint8Array(8);
      const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
      view.setUint16(0, currentOffset, true);
      view.setUint8(2, chunkSize);
      view.setUint8(3, 0);
      view.setUint32(4, this.timestamp, true);

      let reply: Uint8Array;
      try {
        reply = await this.exchange(CMD_EEPROM_READ, payload, REPLY_EEPROM_READ);
      } catch (error) {
        if (!isReadTimeout(error)) {
          throw error;
        }

        // A receiver that has just rebooted can complete session setup but
        // briefly miss its first EEPROM request over Web Serial. EEPROM reads
        // are side-effect free, so retry this request once after it settles.
        await new Promise<void>((resolve) => setTimeout(resolve, EEPROM_READ_RETRY_DELAY_MS));
        reply = await this.exchange(CMD_EEPROM_READ, payload, REPLY_EEPROM_READ);
      }
      if (reply.length < 4) {
        throw new Error("EEPROM reply too short");
      }

      const replyView = new DataView(reply.buffer, reply.byteOffset, reply.byteLength);
      const replyOffset = replyView.getUint16(0, true);
      const replySize = replyView.getUint8(2);
      const data = reply.slice(4);

      if (replyOffset !== currentOffset) {
        throw new Error(
          `EEPROM reply offset mismatch: expected 0x${currentOffset.toString(16)}, got 0x${replyOffset.toString(16)}`
        );
      }
      if (replySize !== data.length) {
        throw new Error(
          `EEPROM reply size mismatch: header=${replySize}, actual=${data.length}`
        );
      }

      chunks.push(data);
      currentOffset += replySize;
      remaining -= replySize;

      if (replySize === 0) {
        throw new Error("EEPROM reply returned zero bytes");
      }
    }

    return concatArrays(chunks);
  }

  /**
   * Writes one EEPROM chunk at `offset`.
   *
   * Firmware expects chunk sizes up to 128 bytes and processes writes in 8-byte
   * units. Callers should provide 8-byte aligned chunks for predictable behavior.
   */
  async writeEeprom(offset: number, data: Uint8Array, allowPassword = false): Promise<void> {
    if (offset < 0 || offset > 0xffff) {
      throw new Error("offset must fit in 16 bits");
    }
    if (data.length <= 0 || data.length > MAX_EEPROM_CHUNK) {
      throw new Error(`data length must be between 1 and ${MAX_EEPROM_CHUNK}`);
    }
    if ((data.length & 0x07) !== 0) {
      throw new Error("data length must be a multiple of 8");
    }

    const payload = new Uint8Array(8 + data.length);
    const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
    view.setUint16(0, offset, true);
    view.setUint8(2, data.length);
    view.setUint8(3, allowPassword ? 1 : 0);
    view.setUint32(4, this.timestamp, true);
    payload.set(data, 8);

    const reply = await this.exchange(CMD_EEPROM_WRITE, payload, REPLY_EEPROM_WRITE);
    if (reply.length < 2) {
      throw new Error("EEPROM write reply too short");
    }
    const replyOffset = new DataView(reply.buffer, reply.byteOffset, reply.byteLength).getUint16(0, true);
    if (replyOffset !== offset) {
      throw new Error(
        `EEPROM write reply offset mismatch: expected 0x${offset.toString(16)}, got 0x${replyOffset.toString(16)}`
      );
    }
  }

  async readScreenshotInfo(): Promise<ScreenshotInfo> {
    const payload = new Uint8Array(4);
    new DataView(payload.buffer, payload.byteOffset, payload.byteLength).setUint32(
      0,
      this.timestamp,
      true
    );
    const reply = await this.exchange(CMD_SCREENSHOT_INFO, payload, REPLY_SCREENSHOT_INFO);
    if (reply.length < 8) {
      throw new Error(`Screenshot info reply too short: ${reply.length}`);
    }
    const view = new DataView(reply.buffer, reply.byteOffset, reply.byteLength);
    return {
      width: view.getUint16(0, true),
      height: view.getUint16(2, true),
      totalSize: view.getUint16(4, true),
      pageCount: view.getUint8(6),
      format: view.getUint8(7)
    };
  }

  async readScreenshot(
    chunkSize = MAX_SCREENSHOT_CHUNK
  ): Promise<{ info: ScreenshotInfo; data: Uint8Array }> {
    if (chunkSize <= 0 || chunkSize > MAX_SCREENSHOT_CHUNK) {
      throw new Error(
        `chunkSize must be between 1 and ${MAX_SCREENSHOT_CHUNK}`
      );
    }
    const info = await this.readScreenshotInfo();
    const chunks: Uint8Array[] = [];
    let offset = 0;

    while (offset < info.totalSize) {
      const thisChunkSize = Math.min(chunkSize, info.totalSize - offset);
      const payload = new Uint8Array(8);
      const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
      view.setUint16(0, offset, true);
      view.setUint8(2, thisChunkSize);
      view.setUint8(3, 0);
      view.setUint32(4, this.timestamp, true);

      const reply = await this.exchange(CMD_SCREENSHOT_CHUNK, payload, REPLY_SCREENSHOT_CHUNK);
      if (reply.length < 4) {
        throw new Error("Screenshot chunk reply too short");
      }
      const replyView = new DataView(reply.buffer, reply.byteOffset, reply.byteLength);
      const replyOffset = replyView.getUint16(0, true);
      const replySize = replyView.getUint8(2);
      const chunk = reply.slice(4);

      if (replyOffset !== offset) {
        throw new Error(
          `Screenshot reply offset mismatch: expected 0x${offset.toString(16).toUpperCase()}, got 0x${replyOffset.toString(16).toUpperCase()}`
        );
      }
      if (replySize !== chunk.length) {
        throw new Error(
          `Screenshot reply size mismatch: header=${replySize}, actual=${chunk.length}`
        );
      }
      if (replySize === 0) {
        throw new Error("Screenshot reply returned zero bytes");
      }

      chunks.push(chunk);
      offset += replySize;
    }

    return {
      info,
      data: concatArrays(chunks)
    };
  }

  /**
   * Requests a device reboot.
   *
   * Firmware handles command `0x05DD` immediately and may reboot before any
   * reply would be possible, so this command is fire-and-forget.
   */
  async requestReset(): Promise<void> {
    await this.sendNoReply(CMD_RESET, new Uint8Array(0));
  }

  /**
   * Lightweight liveness probe.
   *
   * Uses ADC read command (`0x0529`) which is side-effect free relative to
   * session-init commands that can alter UI/backlight behavior.
   */
  async probeAdc(): Promise<void> {
    const reply = await this.exchange(CMD_READ_ADC, new Uint8Array(0), REPLY_READ_ADC);
    if (reply.length < 4) {
      throw new Error(`ADC probe reply too short: ${reply.length}`);
    }
  }

  /** Resets boot-logo flash slot to firmware default image. */
  async resetLogo(): Promise<void> {
    const payload = new Uint8Array(4);
    new DataView(payload.buffer, payload.byteOffset, payload.byteLength).setUint32(
      0,
      this.timestamp,
      true
    );
    const reply = await this.exchange(CMD_LOGO_RESET, payload, REPLY_LOGO_RESET);
    const status = reply.length > 0 ? reply[0] : 0xff;
    if (status !== 0) {
      throw new Error(`Logo reset failed with status ${status}`);
    }
  }
}
